import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  FormGroup,
  Form,
  Input,
  Row,
  Col,
  CardText,
} from "reactstrap";
import {
  useFetchCompanyUserDataQuery,
  useEditCompanyDataMutation,
  useFetchAbexIntegrationQuery,
  useUpsertAbexIntegrationMutation,
} from "api/usersApi";
import EmployeesTable from "components/Companies/employeesTable";
import logo from "../assets/img/black-white-logo.png";
import { getApiErrorMessage } from "../api/errorUtils";

const COMPANY_EDIT_ROLES = new Set(["admin", "owner"]);
const COMPANY_INTEGRATION_ROLES = new Set(["admin", "owner", "manager"]);

function CompanyProfile() {
  const [companyData, setCompanyData] = useState({});
  const [companyUserData, setCompanyUserData] = useState({ user: {}, company: {} });
  const {
    data: userDataResponse,
    error,
    isLoading,
  } = useFetchCompanyUserDataQuery();
  const [editCompanyData] = useEditCompanyDataMutation();
  const {
    data: abexIntegration,
    isLoading: isLoadingAbexIntegration,
  } = useFetchAbexIntegrationQuery();
  const [upsertAbexIntegration] = useUpsertAbexIntegrationMutation();

  // Controlled input fields
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [abexToken, setAbexToken] = useState("");
  const [abexActive, setAbexActive] = useState(true);
  const [isSavingAbexIntegration, setIsSavingAbexIntegration] = useState(false);

  useEffect(() => {
    if (userDataResponse?.company) {
      setCompanyUserData(userDataResponse);
      const company = userDataResponse.company;
      setCompanyData(company);
      setPhone(company.phone || "");
      setAddress(company.address || "");
    }
  }, [userDataResponse]);

  useEffect(() => {
    if (abexIntegration?.isConfigured) {
      setAbexActive(Boolean(abexIntegration.isActive));
      return;
    }

    if (abexIntegration) {
      // New integrations should default to active unless the user explicitly disables them.
      setAbexActive(true);
    }
  }, [abexIntegration]);

  const currentRole = String(companyUserData?.user?.role || "").toLowerCase();
  const canEditCompany = COMPANY_EDIT_ROLES.has(currentRole);
  const canManageAbexIntegration = COMPANY_INTEGRATION_ROLES.has(currentRole);

  if (isLoading) return <div>Loading...</div>;
  if (error)
    return (
      <div>
        Error: {getApiErrorMessage(error, "Unable to load company profile")}
      </div>
    );

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsSaving(true);
      await editCompanyData({
        phone,
        address,
      }).unwrap();
      alert("Company info updated successfully.");
    } catch (err) {
      console.error("Failed to update:", err);
      alert(
        getApiErrorMessage(err, "Error updating company info.", {
          403: "Only company admins or owners can update company information.",
        })
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAbexIntegration = async (e) => {
    e.preventDefault();

    try {
      setIsSavingAbexIntegration(true);
      await upsertAbexIntegration({
        ...(abexToken.trim() ? { apiToken: abexToken.trim() } : {}),
        isActive: abexActive,
      }).unwrap();
      setAbexToken("");
      alert("ABEX integration saved successfully.");
    } catch (err) {
      console.error("Failed to update ABEX integration:", err);
      alert(
        getApiErrorMessage(err, "Error saving ABEX integration.", {
          403: "Only company managers, admins, or owners can manage ABEX integration.",
          409: "ABEX integration needs a valid token before it can be activated.",
        })
      );
    } finally {
      setIsSavingAbexIntegration(false);
    }
  };

  return (
    <div className="content">
      <Row>
        <Col md="8">
          <Card>
            <CardHeader>
              <h5 className="title">Company Profile</h5>
            </CardHeader>
            <CardBody>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <label>Company Name</label>
                      <Input
                        defaultValue={companyData.name}
                        disabled
                        type="text"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label>Registration Number</label>
                      <Input
                        defaultValue={companyData.registration_number || companyData.id || ""}
                        disabled
                        type="text"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <label>Administrator</label>
                      <Input
                        defaultValue={
                          companyData.admin ||
                          companyUserData?.user?.username ||
                          companyUserData?.user?.email ||
                          ""
                        }
                        disabled
                        type="text"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label>Email</label>
                      <Input
                        defaultValue={companyData.email}
                        disabled
                        type="email"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <label>Phone</label>
                      <Input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Phone"
                        type="text"
                        disabled={!canEditCompany || isSaving}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label>Registered At</label>
                      <Input
                        defaultValue={companyData.created_at?.slice(0, 10)}
                        disabled
                        type="text"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="12">
                    <FormGroup>
                      <label>Address</label>
                      <Input
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Address"
                        type="text"
                        disabled={!canEditCompany || isSaving}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                {!canEditCompany && (
                  <p className="text-warning mb-0">
                    Company contact details can be edited only by company admins
                    or owners.
                  </p>
                )}
              </Form>
            </CardBody>
            <CardFooter>
              <Button
                className="btn-fill"
                color="primary"
                type="submit"
                onClick={handleSubmit}
                disabled={!canEditCompany || isSaving}
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </CardFooter>
          </Card>
        </Col>

        <Col md="4">
          <Card className="card-user">
            <CardBody>
              <CardText />
              <div className="author">
                <div className="block block-one" />
                <div className="block block-two" />
                <div className="block block-three" />
                <div className="block block-four" />
                <a href="#company" onClick={(e) => e.preventDefault()}>
                  <img alt="company-logo" className="avatar" src={logo} />
                  <h5 className="title">{companyData.name}</h5>
                </a>
                <p className="description">Registered Company</p>
              </div>
              <div className="card-description">
                Manage your company’s core information here. Keep everything
                accurate and up to date.
              </div>
            </CardBody>
            <CardFooter>
              <div className="button-container">
                <Button className="btn-icon btn-round" color="linkedin">
                  <i className="fab fa-linkedin" />
                </Button>
                <Button className="btn-icon btn-round" color="envelope">
                  <i className="fas fa-envelope" />
                </Button>
                <Button className="btn-icon btn-round" color="globe">
                  <i className="fas fa-globe" />
                </Button>
              </div>
            </CardFooter>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <h5 className="title">ABEX Integration</h5>
            </CardHeader>
            <CardBody>
              {isLoadingAbexIntegration ? (
                <div>Loading integration...</div>
              ) : (
                <Form onSubmit={handleSaveAbexIntegration}>
                  <p className="text-muted">
                    Configure this company&apos;s courier token. Orders and
                    shipments stay disabled until ABEX is active for this
                    company.
                  </p>

                  <FormGroup>
                    <label>Status</label>
                    <Input
                      value={
                        abexIntegration?.isConfigured
                          ? abexIntegration?.isActive
                            ? "Active"
                            : "Configured but inactive"
                          : "Not configured"
                      }
                      disabled
                      type="text"
                    />
                  </FormGroup>

                  <FormGroup>
                    <label>Stored Token</label>
                    <Input
                      value={
                        abexIntegration?.tokenLast4
                          ? `••••${abexIntegration.tokenLast4}`
                          : "No token stored"
                      }
                      disabled
                      type="text"
                    />
                  </FormGroup>

                  <FormGroup>
                    <label>New Token</label>
                    <Input
                      value={abexToken}
                      onChange={(e) => setAbexToken(e.target.value)}
                      placeholder="Paste ABEX API token"
                      type="password"
                      disabled={!canManageAbexIntegration || isSavingAbexIntegration}
                    />
                  </FormGroup>

                  <FormGroup check className="mb-3">
                    <label className="d-block mb-2">Activation</label>
                    <div className="d-flex align-items-center justify-content-between border rounded px-3 py-2">
                      <div>
                        <div className="font-weight-bold">
                          {abexActive ? "ABEX is active" : "ABEX is inactive"}
                        </div>
                        <small className="text-muted">
                          {abexActive
                            ? "This company can create and manage shipments."
                            : "Orders and shipments stay disabled until you activate ABEX."}
                        </small>
                      </div>
                      <Button
                        color={abexActive ? "success" : "secondary"}
                        type="button"
                        className="mb-0"
                        onClick={() => setAbexActive((current) => !current)}
                        disabled={!canManageAbexIntegration || isSavingAbexIntegration}
                      >
                        {abexActive ? "Active" : "Inactive"}
                      </Button>
                    </div>
                  </FormGroup>

                  {!canManageAbexIntegration && (
                    <p className="text-warning mb-3">
                      Only company managers, admins, or owners can manage ABEX
                      integration.
                    </p>
                  )}

                  <Button
                    className="btn-fill"
                    color="info"
                    type="submit"
                    disabled={!canManageAbexIntegration || isSavingAbexIntegration}
                  >
                    {isSavingAbexIntegration ? "Saving..." : "Save ABEX Integration"}
                  </Button>
                </Form>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col md="12">
          <EmployeesTable companyId={companyData.id} />
        </Col>
      </Row>
    </div>
  );
}

export default CompanyProfile;
